import React, { useContext, useState } from 'react';
import { Button, Icons, CircularProgress } from '@nextiva/next-ui-components';
import { useQueryClient } from 'react-query';
import { useGetLicensePricingData } from 'hooks/useGetLicensePricingData/useGetLicensePricingData';
import { useProcessLicenseOrder } from 'hooks/useProcessLicenseOrder/useProcessLicenseOrder';
import {
  processLicenseOrderPayloadType,
  licensePricingQueryDataType,
  licenseOrderErrorResponseType,
} from 'types/purchaseLicenseModalTypes';
import { formatLicensePricingData } from 'utils/licensePricingDataFormatter';
import { LICENSE_QUERY_KEY } from 'constants/purchaseLicenseModalConstants';
import { FeatureContext } from 'components/AccessResolvers/featureContext';
import {
  LicensesContext,
  actionTypes,
} from '../../containers/pages/BillingLicenses/licensesContext/index';
import {
  StyledDialog,
  StyledDialogTitle,
  StyledDialogActions,
  StyledCancelButton,
  StyledDividerContainer,
  StyledDivider,
  StyledDialogContent,
  StyledTitleIcon,
  StyledContentDividerContainer,
  StyledContentDivider,
  StyledPaymentContainer,
  StyledLoadingSpinnerContainer,
} from './PurchaseLicenseModal.styles';
import { CreditCardInfo } from './components/CreditCardInfo';
import { LicenseQuantity } from './components/LicenseQuantity';
import { RecurringCharges } from './components/RecurringCharges';
import { TotalDue } from './components/TotalDue';
import { MonthlyBill } from './components/MonthlyBill';
import { ShowBillingDetails } from './components/ShowBillingDetails';
import { TermsAndConditions } from './components/TermsAndConditions';
import { ExpiredPaymentModal } from './components/ExpiredPaymentModal';
import { GradientLoadingSpinner } from './components/GradientLoadingSpinner';
import { MultipleBillingLocationsModal } from './components/MultipleBillingLocationsModal';
import { OpenQuoteModal } from './components/OpenQuoteModal';
import { NonBmlSupportedModal } from './components/NonBmlSupportedModal';
import {
  InvalidBillingInfoErrorBanner,
  GeneralErrorBanner,
  DeclinedPaymentErrorBanner,
  ExpiredPaymentErrorBanner,
  PaymentMethodNotSupportedErrorBanner,
  LicensePriceNotValidErrorBanner,
  DefaultPaymentMethodDoesNotExistErrorBanner,
} from './components/PurchaseLicenseModalErrorBanners';

const PAYMENT_DECLINED_TEXT = 'Payment was not successful.';
const MULTIPLE_BILLING_SITE_ERROR_CODE = 'MULTIPLE_BILLING_SITE_NOT_ALLOWED';
const INVALID_BILLING_INFO_ERROR_CODE = 'INVALID_BILLING_INFO';
const OPEN_QUOTE_ERROR_CODE = 'QUOTE_IN_PROGRESS';
const PAYMENT_METHOD_NOT_SUPPORTED_ERROR_CODE = 'PAYMENT_METHOD_NOT_SUPPORTED';
const LICENSE_PRICE_NOT_VALID_ERROR_CODE = 'LICENSE_PRICE_NOT_VALID';

export const PurchaseLicenseModal = () => {
  const [quantity, setQuantity] = useState('1');
  const [expiredPaymentDialogOpen, setExpiredPaymentDialogOpen] = useState(
    false,
  );
  const [loadingSpinnerActive, setLoadingSpinnerActive] = useState(false);
  const [paymentDeclined, setPaymentDeclined] = useState(false);
  const [paymentTransactionFailed, setPaymentTransactionFailed] = useState(
    false,
  );

  const queryClient = useQueryClient();

  const {
    dispatch,
    licensesState: { purchaseLicenseAssignment },
  } = useContext(LicensesContext);
  const {
    featureState: { authV3Enabled },
  } = useContext(FeatureContext);

  const {
    data: licensePricingData,
    error: licensePricingDataErrorResponse,
    isLoading,
    isError: didInitialLicensePricingDataApiCallFail,
  }: any = useGetLicensePricingData(purchaseLicenseAssignment?.id, quantity);
  const errorCode = licensePricingDataErrorResponse?.response?.data?.errorCode;
  const processLicenseOrder = useProcessLicenseOrder(
    purchaseLicenseAssignment?.id,
  );

  const formattedLicensePricingData = formatLicensePricingData(
    licensePricingData?.data,
    Number(quantity),
  );

  const defaultPaymentMethodDoesNotExist =
    !licensePricingData?.data?.paymentMethod?.paymentMethodId &&
    !licensePricingData?.data?.paymentMethod?.paymentMethodType &&
    !didInitialLicensePricingDataApiCallFail;
  const isPaymentExpired = formattedLicensePricingData?.isPaymentMethodExpired;
  const isPaymentButtonDisabled =
    Number(quantity) === 0 ||
    loadingSpinnerActive ||
    isPaymentExpired ||
    didInitialLicensePricingDataApiCallFail ||
    defaultPaymentMethodDoesNotExist;

  const togglePurchaseLicenseModal = (): void => {
    setQuantity('1');
    setPaymentTransactionFailed(false);
    setPaymentDeclined(false);
    dispatch({
      type: actionTypes.TOGGLE_PURCHASE_LICENSE_MODAL,
    });
  };

  const togglePurchaseLicenseConfirmationPage = (): void => {
    dispatch({
      type: actionTypes.TOGGLE_PURCHASE_LICENSE_CONFIRMATION_PAGE,
    });
  };

  const onPayButtonClick = (): void => {
    setLoadingSpinnerActive(true);

    const cachedLicensePricingData: licensePricingQueryDataType = queryClient.getQueryData(
      [LICENSE_QUERY_KEY, purchaseLicenseAssignment?.id],
    );

    const processLicenseOrderPayload: processLicenseOrderPayloadType = {
      billingSiteId: cachedLicensePricingData?.data?.billingSiteId,
      originalQuantity: authV3Enabled
        ? purchaseLicenseAssignment?.licenseCount
        : purchaseLicenseAssignment?.license_count,
      paymentMethodId: Number(
        cachedLicensePricingData?.data?.paymentMethod?.paymentMethodId,
      ),
      paymentMethodType:
        cachedLicensePricingData?.data?.paymentMethod?.paymentMethodType,
      requestedQuantity: Number(quantity),
      serviceLocationSiteId:
        cachedLicensePricingData?.data?.serviceLocationSiteId,
    };

    processLicenseOrder.mutate(processLicenseOrderPayload, {
      onSuccess: (): void => {
        setLoadingSpinnerActive(false);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        queryClient.invalidateQueries(['licenseList']);
        togglePurchaseLicenseConfirmationPage();
        togglePurchaseLicenseModal();
      },
      onError: (error: licenseOrderErrorResponseType): void => {
        setLoadingSpinnerActive(false);
        if (error?.response?.data?.message === PAYMENT_DECLINED_TEXT) {
          setPaymentDeclined(true);
        } else {
          setPaymentTransactionFailed(true);
        }
      },
    });
  };

  if (errorCode === MULTIPLE_BILLING_SITE_ERROR_CODE) {
    return <MultipleBillingLocationsModal />;
  }

  if (errorCode === OPEN_QUOTE_ERROR_CODE) {
    return <OpenQuoteModal />;
  }

  if (licensePricingData?.data?.bmlSupported === false) {
    return <NonBmlSupportedModal />;
  }

  return (
    <>
      {expiredPaymentDialogOpen && (
        <ExpiredPaymentModal
          setExpiredPaymentDialogOpen={setExpiredPaymentDialogOpen}
        />
      )}
      <StyledDialog
        aria-labelledby="Purchase licenses"
        open
        data-testid="PlatformAdminUI-PurchaseLicenseModal"
      >
        {isLoading ? (
          <StyledLoadingSpinnerContainer>
            <CircularProgress />
          </StyledLoadingSpinnerContainer>
        ) : (
          <>
            <StyledDialogTitle>
              Purchase licenses
              <StyledTitleIcon icon={Icons.shoppingCart} />
            </StyledDialogTitle>
            <StyledDividerContainer>
              <StyledDivider />
            </StyledDividerContainer>
            <StyledDialogContent>
              <LicenseQuantity
                quantity={quantity}
                setQuantity={setQuantity}
                formattedLicensePricingData={formattedLicensePricingData}
                didInitialLicensePricingDataApiCallFail={
                  didInitialLicensePricingDataApiCallFail
                }
                defaultPaymentMethodDoesNotExist={
                  defaultPaymentMethodDoesNotExist
                }
              />
              <StyledContentDividerContainer>
                <StyledContentDivider />
              </StyledContentDividerContainer>
              {didInitialLicensePricingDataApiCallFail &&
                errorCode === INVALID_BILLING_INFO_ERROR_CODE && (
                  <InvalidBillingInfoErrorBanner />
                )}
              {didInitialLicensePricingDataApiCallFail &&
                errorCode === PAYMENT_METHOD_NOT_SUPPORTED_ERROR_CODE && (
                  <PaymentMethodNotSupportedErrorBanner />
                )}
              {didInitialLicensePricingDataApiCallFail &&
                errorCode === LICENSE_PRICE_NOT_VALID_ERROR_CODE && (
                  <LicensePriceNotValidErrorBanner />
                )}
              {!didInitialLicensePricingDataApiCallFail && isPaymentExpired && (
                <ExpiredPaymentErrorBanner
                  setExpiredPaymentDialogOpen={setExpiredPaymentDialogOpen}
                />
              )}
              {!didInitialLicensePricingDataApiCallFail && paymentDeclined && (
                <DeclinedPaymentErrorBanner
                  setExpiredPaymentDialogOpen={setExpiredPaymentDialogOpen}
                />
              )}
              {!didInitialLicensePricingDataApiCallFail &&
                defaultPaymentMethodDoesNotExist && (
                  <DefaultPaymentMethodDoesNotExistErrorBanner />
                )}
              {(paymentTransactionFailed ||
                (didInitialLicensePricingDataApiCallFail &&
                  errorCode !== INVALID_BILLING_INFO_ERROR_CODE &&
                  errorCode !== PAYMENT_METHOD_NOT_SUPPORTED_ERROR_CODE &&
                  errorCode !== LICENSE_PRICE_NOT_VALID_ERROR_CODE)) && (
                <GeneralErrorBanner />
              )}
              <StyledPaymentContainer>
                <CreditCardInfo
                  isPaymentExpired={isPaymentExpired}
                  formattedLicensePricingData={formattedLicensePricingData}
                  didInitialLicensePricingDataApiCallFail={
                    didInitialLicensePricingDataApiCallFail
                  }
                  defaultPaymentMethodDoesNotExist={
                    defaultPaymentMethodDoesNotExist
                  }
                />
                <RecurringCharges
                  formattedLicensePricingData={formattedLicensePricingData}
                  didInitialLicensePricingDataApiCallFail={
                    didInitialLicensePricingDataApiCallFail
                  }
                />
              </StyledPaymentContainer>
              <MonthlyBill
                formattedLicensePricingData={formattedLicensePricingData}
                didInitialLicensePricingDataApiCallFail={
                  didInitialLicensePricingDataApiCallFail
                }
              />
              <ShowBillingDetails
                quantity={quantity}
                formattedLicensePricingData={formattedLicensePricingData}
                didInitialLicensePricingDataApiCallFail={
                  didInitialLicensePricingDataApiCallFail
                }
              />
              <TotalDue
                formattedLicensePricingData={formattedLicensePricingData}
                didInitialLicensePricingDataApiCallFail={
                  didInitialLicensePricingDataApiCallFail
                }
              />
              <TermsAndConditions />
            </StyledDialogContent>
            <StyledDividerContainer>
              <StyledDivider />
            </StyledDividerContainer>
            <StyledDialogActions>
              <StyledCancelButton
                color="secondary"
                variant="contained"
                onClick={() => togglePurchaseLicenseModal()}
                data-testid="PlatformAdminUI-PurchaseLicenseModal-CancelButton"
              >
                Cancel
              </StyledCancelButton>
              <Button
                color="primary"
                variant="contained"
                data-testid="PlatformAdminUI-PurchaseLicenseModal-PayButton"
                disabled={isPaymentButtonDisabled}
                onClick={() => onPayButtonClick()}
              >
                {loadingSpinnerActive && <GradientLoadingSpinner />}
                Pay{' '}
                {didInitialLicensePricingDataApiCallFail
                  ? '$--'
                  : formattedLicensePricingData?.totalDueToday}
              </Button>
            </StyledDialogActions>
          </>
        )}
      </StyledDialog>
    </>
  );
};
